import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, ListCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import { absoluteReportUrl, type ReportSchedule } from '@/services/reports/reportsApi'
import { cadenceText, nextRunText } from '@/services/reports/reportCadence'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  template: 'reportTemplate.name',
  cadence: 'recurrence',
  nextRun: 'scheduledFor',
  objectState: 'objectState',
  updateTime: 'updateTime',
}

interface ReportScheduleTableProps {
  schedules: ReportSchedule[]
  loading?: boolean
  emptyMessage?: string
  selection?: ReportSchedule[]
  onSelectionChange?: (schedules: ReportSchedule[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function ReportScheduleTable({
  schedules,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: ReportScheduleTableProps) {
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<ReportSchedule, unknown>[] = [
    ...(selectable ? [selectionColumn<ReportSchedule>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 220 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      id: 'template',
      accessorFn: (row) => row.templateName,
      header: t('reports.template'),
      meta: { minWidth: 180 },
      cell: ({ row }) =>
        row.original.templateId ? (
          <Link
            to={`/reports/templates/${row.original.templateId}`}
            className="text-xs text-(--ad-link)"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.templateName || row.original.templateId}
          </Link>
        ) : (
          <MutedCell />
        ),
    },
    {
      id: 'cadence',
      accessorFn: (row) => row.recurrence ?? '',
      header: t('reports.cadenceLabel'),
      size: 170,
      cell: ({ row }) => <MutedCell value={cadenceText(row.original, t)} />,
    },
    {
      id: 'nextRun',
      accessorFn: (row) => row.scheduledFor,
      header: t('reportSchedules.nextRun'),
      size: 200,
      cell: ({ row }) => <MutedCell value={nextRunText(row.original, t)} />,
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    },
    optionalColumn<ReportSchedule>({
      id: 'recipients',
      accessorFn: (row) => row.recipientUsers.length + row.recipientEmailAddresses.length,
      header: t('reportSchedules.recipients'),
      size: 200,
      cell: ({ row }) => (
        <ListCell
          values={[
            ...row.original.recipientUsers.map((u) => u.name || u.id),
            ...row.original.recipientEmailAddresses,
          ]}
        />
      ),
    }),
    optionalColumn<ReportSchedule>({
      id: 'reportUrl',
      accessorFn: (row) => row.reportUrl,
      header: t('reportSchedules.latestReportUrl'),
      enableSorting: false,
      meta: { minWidth: 200 },
      cell: ({ row }) =>
        row.original.reportUrl ? (
          <a
            href={absoluteReportUrl(row.original.reportUrl)}
            target="_blank"
            rel="noreferrer"
            className="text-xs break-all text-(--ad-link)"
            onClick={(e) => e.stopPropagation()}
          >
            {absoluteReportUrl(row.original.reportUrl)}
          </a>
        ) : (
          <MutedCell />
        ),
    }),
    optionalColumn<ReportSchedule>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={schedules}
      loading={loading}
      emptyMessage={emptyMessage ?? t('reportSchedules.noSchedulesFound')}
      columnsKey="reportSchedules"
      rowHref={(row) => `/reports/schedules/${row.id}`}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
